import { useEffect, useRef, useState, type InputHTMLAttributes } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FolderUp, UploadCloud } from "lucide-react";

import { Modal } from "@/components/ui/Modal";

import {
  createDirResolver,
  treeFromDataTransfer,
  treeFromFileList,
  uploadFile,
  type UploadTree,
} from "../upload";
import { formatBytes } from "../utils";

interface UploadEntry {
  id: string;
  name: string;
  size: number;
  pct: number;
  done?: boolean;
  error?: boolean;
}

export type UploadMode = "files" | "folder";

interface Props {
  parent: string | null;
  open: boolean;
  mode: UploadMode;
  onClose: () => void;
}

// React has no typing for the non-standard (but universally supported) folder-picker attribute.
const folderInputProps = { webkitdirectory: "" } as unknown as InputHTMLAttributes<HTMLInputElement>;

/**
 * Modal with a large drag-and-drop area that uploads into `parent`. In "folder"
 * mode the picker selects a directory; dropped directories are recursed in both
 * modes and their structure is recreated on the server before the files upload.
 */
export function UploadDialog({ parent, open, mode, onClose }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState<UploadEntry[]>([]);

  // Fresh list every time the dialog is opened.
  useEffect(() => {
    if (open) setUploads([]);
  }, [open]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["two-drive", "nodes", parent ?? "root"] });
    qc.invalidateQueries({ queryKey: ["two-drive", "quota"] });
  }

  async function handleTree({ uploads: pending, dirs }: UploadTree) {
    const resolveDir = createDirResolver(parent);

    // Ensure every directory up front (covers empty ones); failures surface as entries.
    for (const dir of dirs) {
      try {
        await resolveDir(dir);
      } catch {
        setUploads((u) => [
          ...u,
          { id: crypto.randomUUID(), name: `${dir.join("/")}/`, size: 0, pct: 0, error: true },
        ]);
      }
    }
    if (dirs.length) invalidate();

    for (const { file, dir } of pending) {
      const id = crypto.randomUUID();
      const label = [...dir, file.name].join("/");
      setUploads((u) => [...u, { id, name: label, size: file.size, pct: 0 }]);
      try {
        const target = await resolveDir(dir);
        await uploadFile(file, target, (pct) =>
          setUploads((u) => u.map((e) => (e.id === id ? { ...e, pct } : e))),
        );
        setUploads((u) => u.map((e) => (e.id === id ? { ...e, pct: 100, done: true } : e)));
        invalidate();
      } catch {
        setUploads((u) => u.map((e) => (e.id === id ? { ...e, error: true } : e)));
      }
    }
  }

  const isFolder = mode === "folder";

  return (
    <Modal open={open} onClose={onClose} title={isFolder ? "Prześlij folder" : "Wgraj pliki"} size="lg">
      <div className="flex flex-col gap-4">
        <div
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            // Entries must be read synchronously, before the promise resolves.
            treeFromDataTransfer(e.dataTransfer).then(handleTree);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
          }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed min-h-[220px] flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors px-6 py-10 text-center ${
            dragOver
              ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
              : "border-[var(--color-border-strong)] hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-base)]"
          }`}
        >
          {isFolder ? (
            <FolderUp size={44} className="text-[var(--color-text-muted)]" />
          ) : (
            <UploadCloud size={44} className="text-[var(--color-text-muted)]" />
          )}
          <p className="text-sm text-[var(--color-text-secondary)]">
            {isFolder
              ? "Przeciągnij folder tutaj lub kliknij, aby wybrać"
              : "Przeciągnij pliki tutaj lub kliknij, aby wybrać"}
          </p>
          <p className="text-xs font-mono text-[var(--color-text-muted)]">
            {isFolder
              ? "Struktura katalogów zostanie zachowana"
              : "Możesz wgrać wiele plików naraz"}
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            {...(isFolder ? folderInputProps : {})}
            onChange={(e) => {
              if (e.target.files?.length) handleTree(treeFromFileList(e.target.files));
              e.target.value = "";
            }}
          />
        </div>

        {uploads.length > 0 && (
          <ul className="flex flex-col gap-1 max-h-64 overflow-y-auto">
            {uploads.map((u) => (
              <li key={u.id} className="border border-[var(--color-border)] px-3 py-2">
                <div className="flex items-center justify-between text-xs font-mono mb-1 gap-2">
                  <span className="truncate flex items-center gap-1.5">
                    {u.done && <CheckCircle2 size={12} className="text-green-600 flex-shrink-0" />}
                    <span className="truncate" title={u.name}>{u.name}</span>
                  </span>
                  <span className={u.error ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"}>
                    {u.error ? "Błąd" : u.done ? formatBytes(u.size) : `${u.pct}%`}
                  </span>
                </div>
                <div className="h-1.5 bg-[var(--color-bg-base)] overflow-hidden">
                  <div
                    className={`h-full transition-[width] ${
                      u.error ? "bg-[var(--color-primary)]" : u.done ? "bg-green-600" : "bg-blue-600"
                    }`}
                    style={{ width: `${u.pct}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
